import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, Leaf, Clock, ChevronDown } from "lucide-react";
import { IMAGES } from "../../data/content";
import { LotusMark } from "./LotusMark";
import { site } from "../../config/site";
import { scrollToSection as scrollTo } from "./lenis";


const HEADLINE = ["Every", "Property", "Deserves", "to Bloom."];

const RotatingStamp = () => (
  <motion.div
    className="relative w-32 h-32 md:w-40 md:h-40"
    animate={{ rotate: 360 }}
    transition={{ duration: 22, ease: "linear", repeat: Infinity }}
  >
    <svg viewBox="0 0 200 200" className="w-full h-full">
      <defs>
        <path id="circlePath" d="M 100,100 m -74,0 a 74,74 0 1,1 148,0 a 74,74 0 1,1 -148,0" />
      </defs>
      <text fill="rgba(255,255,255,0.85)" fontSize="15" fontWeight="600" letterSpacing="3" fontFamily="League Spartan">
        <textPath href="#circlePath" startOffset="0%">
          EVERY PROPERTY · DESERVES TO BLOOM ·
        </textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <LotusMark size={46} />
    </div>
  </motion.div>
);

const trust = [
  { icon: ShieldCheck, color: "#F56FB4", title: "Licensed & Insured", sub: "Full peace of mind." },
  { icon: Leaf, color: "#53C653", title: "Eco-Friendly", sub: "We donate & recycle." },
  { icon: Clock, color: "#F56FB4", title: "Fast & Reliable", sub: "On-time, every time." },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const petalY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <section id="hero" ref={ref} className="relative min-h-[100svh] overflow-hidden bg-[#111111] pt-[76px]">
      {/* Decorative parallax petal */}
      <motion.div style={{ y: petalY }} className="pointer-events-none absolute -right-24 top-1/3 opacity-[0.07]">
        <LotusMark size={520} />
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-8 items-center min-h-[calc(100svh-76px)] py-10">
        {/* Left — copy */}
        <motion.div style={{ y: textY }} className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-2 mb-5"
          >
            <span className="h-px w-8 bg-[#53C653]" />
            <span className="text-xs md:text-sm tracking-[0.28em] uppercase font-semibold text-[#53C653] font-heading">
              Chester, PA&apos;s Trusted Cleanout Crew
            </span>
          </motion.div>

          <h1 className="font-heading font-extrabold uppercase text-white leading-[0.92] tracking-tight text-5xl md:text-6xl lg:text-7xl">
            {HEADLINE.map((word, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="inline-block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {word === "to Bloom." ? (
                    <span className="text-[#F56FB4]">{word}</span>
                  ) : word}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="font-script text-3xl md:text-4xl text-[#F56FB4] mt-4 mb-5"
          >
            We remove the mess. You enjoy the new.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="text-white/70 max-w-xl text-base md:text-lg leading-relaxed"
          >
            Professional junk removal & property transformation services throughout
            Chester, PA and Delaware County. From clutter to clarity — we help homes
            and businesses bloom.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="flex flex-wrap gap-4 mt-8"
          >
            <button
              data-testid="hero-estimate-btn"
              onClick={() => scrollTo("quote")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#F56FB4] px-8 py-4 font-semibold text-white transition-transform duration-200 hover:scale-105 shadow-[0_0_25px_rgba(245,111,180,0.45)]"
            >
              Get a Free Estimate
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-call-btn"
              href={site.phoneHref || "#quote"}
              onClick={(e) => { if (!site.phoneHref) { e.preventDefault(); scrollTo("quote"); } }}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#53C653] px-8 py-4 font-semibold text-[#53C653] transition-colors duration-200 hover:bg-[#53C653]/10"
            >
              <Phone size={18} /> Call Now
            </a>
          </motion.div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
            {trust.map((t) => (
              <div key={t.title} className="flex items-center gap-3">
                <t.icon size={22} style={{ color: t.color }} />
                <div>
                  <p className="text-sm font-semibold text-white leading-tight">{t.title}</p>
                  <p className="text-xs text-white/50 leading-tight">{t.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right — image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)] aspect-[4/5] md:aspect-[5/6]">
            <motion.img
              src={IMAGES.property}
              alt="Transformed luxury property at dusk"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent" />
            <div className="absolute left-5 bottom-5 rounded-2xl bg-[#111111]/70 backdrop-blur-md border border-white/10 px-5 py-3">
              <p className="text-3xl font-heading font-extrabold text-white leading-none">500<span className="text-[#F56FB4]">+</span></p>
              <p className="text-xs text-white/60 mt-1">Properties Bloomed</p>
            </div>
          </div>
          <div className="absolute -left-6 -top-6 md:-left-10 md:-top-8 z-20">
            <RotatingStamp />
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/50 hover:text-white transition-colors"
        aria-label="Scroll down"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ChevronDown size={20} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
